import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import ThemeToggle from "@/components/ThemeToggle";
import {
  LayoutDashboard, GitBranch, Settings, LogOut,
  Handshake, Sparkles, IndianRupee, BookOpen, Target, FileText, ShieldCheck,
  Menu, X, Link2,
} from "lucide-react";

const SM_NAV = [
  { key: "dashboard",      label: "Dashboard",          to: "/sm",                    end: true, icon: LayoutDashboard },
  { key: "hierarchy",      label: "Partner Network",     to: "/sm/hierarchy",          icon: GitBranch },
  { key: "commission",     label: "Commission Ledger",   to: "/sm/commission-ledger",  icon: IndianRupee },
  { key: "statements",     label: "Monthly Statements",  to: "/sm/statements",         icon: FileText },
  { key: "config",         label: "Commission Config",   to: "/sm/commission-config",  icon: Settings },
  { key: "crm",            label: "CRM & Prospects",     to: "/sm/crm",                icon: Target },
  { key: "referral-links", label: "Referral Links",      to: "/sm/referral-links",     icon: Link2 },
  { key: "kyc",            label: "Partner KYC",         to: "/sm/kyc",                icon: ShieldCheck },
  { key: "content",        label: "Content Library",     to: "/sm/content-library",    icon: BookOpen },
  { key: "ai-guide",       label: "AI Guide",            to: "/sm/ai-guide",           icon: Sparkles },
  { key: "settings",       label: "Settings",            to: "/sm/settings",           icon: Handshake },
];

export default function SMLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useBranding();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [n1, ...rest] = (branding.app_name || "Law OS").split(" ");
  const n2 = rest.join(" ");

  // Close sidebar on navigation
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="h-14 flex items-center gap-2.5 px-4 border-b border-border shrink-0">
        {branding.logo_url
          ? <img src={branding.logo_url} alt="logo" className="h-6 object-contain" />
          : <span className="text-primary font-bold tracking-tight text-base">{n1}<span className="text-foreground font-light">{n2 ? ` ${n2}` : ""}</span></span>
        }
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground ml-auto hidden sm:block">Partner HQ</span>
        {/* Close button on mobile */}
        <button onClick={() => setSidebarOpen(false)} className="ml-auto md:hidden p-1 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {SM_NAV.map(item => (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.end}
            data-testid={`sm-nav-${item.key}`}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-4 py-2.5 mx-2 rounded-lg text-[13px] transition-all mb-0.5 ${
                isActive
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`
            }
          >
            <item.icon className="w-4 h-4 shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Bottom user strip */}
      <div className="p-3 border-t border-border space-y-1 shrink-0">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-[10px] font-bold shrink-0">
            {(user?.name || "S")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium truncate">{user?.name || "SM Admin"}</div>
            <div className="text-[9px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <ThemeToggle />
        </div>
        <button onClick={handleLogout}
          className="w-full flex items-center gap-2 px-2 py-1.5 text-[12px] text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-lg transition-all">
          <LogOut className="w-3.5 h-3.5" />Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">

      {/* ── Mobile backdrop ───────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar — fixed drawer on mobile, static on desktop ─ */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 flex flex-col border-r border-border bg-card
        transform transition-transform duration-300 ease-in-out
        md:relative md:translate-x-0 md:w-56 md:shrink-0
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        <SidebarContent />
      </aside>

      {/* ── Main area ────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center gap-3 px-4 h-14 border-b border-border bg-card shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded-lg hover:bg-muted transition-all text-muted-foreground hover:text-foreground"
            aria-label="Open navigation"
          >
            <Menu className="w-5 h-5" />
          </button>
          {branding.logo_url
            ? <img src={branding.logo_url} alt="logo" className="h-6 object-contain" />
            : <span className="text-primary font-bold text-sm">{n1}<span className="text-foreground font-light">{n2 ? ` ${n2}` : ""}</span></span>
          }
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground ml-1">Partner HQ</span>
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
